import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMaterialService } from "../apis/material/useMaterialService";
import {
  TCreateMaterialGroupRequest,
  TCreateMaterialItemPayload,
  TMaterialGroup,
  TMaterialItem,
  TMaterialListFilters,
  TMaterialListResponse,
  TUpdateMaterialGroupRequest,
  TUpdateMaterialItemPayload,
} from "../apis/material/material.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useMaterialGroupListHook = (
  filters?: Omit<TMaterialListFilters, "instituteId">,
) => {
  const { getMaterialGroups } = useMaterialService();
  const { selectedInstitute } = useInstituteContext();

  const instituteId = selectedInstitute?.id;
  const mergedFilters: TMaterialListFilters = {
    ...filters,
    instituteId,
  };

  return useQuery<TMaterialListResponse>({
    queryKey: ["materials", instituteId, mergedFilters],
    queryFn: () => getMaterialGroups(mergedFilters),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMaterialGroupByIdHook = (id?: string) => {
  const { getMaterialGroupById } = useMaterialService();

  return useQuery<TMaterialGroup>({
    queryKey: ["material-group", id],
    queryFn: () => getMaterialGroupById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

const useInvalidateMaterialQueries = () => {
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return (groupId?: string) => {
    queryClient.invalidateQueries({
      queryKey: ["materials", selectedInstitute?.id],
    });
    if (groupId) {
      queryClient.invalidateQueries({
        queryKey: ["material-group", groupId],
      });
    }
  };
};

export const useCreateMaterialGroupHook = () => {
  const { createMaterialGroup } = useMaterialService();
  const invalidate = useInvalidateMaterialQueries();

  return useMutation<TMaterialGroup, Error, TCreateMaterialGroupRequest>({
    mutationFn: (request) => createMaterialGroup(request),
    onSuccess: (group) => invalidate(group?.id),
  });
};

export const useUpdateMaterialGroupHook = () => {
  const { updateMaterialGroup } = useMaterialService();
  const invalidate = useInvalidateMaterialQueries();

  return useMutation<
    TMaterialGroup,
    Error,
    { materialGroupId: string; request: TUpdateMaterialGroupRequest }
  >({
    mutationFn: ({ materialGroupId, request }) =>
      updateMaterialGroup(materialGroupId, request),
    onSuccess: (_data, vars) => invalidate(vars.materialGroupId),
  });
};

export const useAddMaterialItemHook = () => {
  const { addMaterialItem } = useMaterialService();
  const invalidate = useInvalidateMaterialQueries();

  return useMutation<
    TMaterialItem,
    Error,
    { materialGroupId: string; payload: TCreateMaterialItemPayload }
  >({
    mutationFn: ({ materialGroupId, payload }) =>
      addMaterialItem(materialGroupId, payload),
    onSuccess: (_data, vars) => invalidate(vars.materialGroupId),
  });
};

export const useUpdateMaterialItemHook = () => {
  const { updateMaterialItem } = useMaterialService();
  const invalidate = useInvalidateMaterialQueries();

  return useMutation<
    TMaterialItem,
    Error,
    {
      materialGroupId: string;
      itemId: string;
      payload: TUpdateMaterialItemPayload;
    }
  >({
    mutationFn: ({ materialGroupId, itemId, payload }) =>
      updateMaterialItem(materialGroupId, itemId, payload),
    onSuccess: (_data, vars) => invalidate(vars.materialGroupId),
  });
};

export const useDeleteMaterialItemHook = () => {
  const { deleteMaterialItem } = useMaterialService();
  const invalidate = useInvalidateMaterialQueries();

  return useMutation<
    { success: boolean },
    Error,
    { materialGroupId: string; itemId: string }
  >({
    mutationFn: ({ materialGroupId, itemId }) =>
      deleteMaterialItem(materialGroupId, itemId),
    onSuccess: (_data, vars) => invalidate(vars.materialGroupId),
  });
};
